import {
  DeepKeys,
  DeepValue,
  FieldAsyncValidateOrFn,
  FieldValidateOrFn,
  FieldValidators,
  ValidationError,
} from '@tanstack/react-form'

type SyncRule<FieldValue> = (value: FieldValue) => ValidationError
type AsyncRule<FieldValue> = (value: FieldValue) => Promise<ValidationError> | ValidationError

type CreateFieldValidatorArgs<FieldValue> = SyncRule<FieldValue>[] | [SyncRule<FieldValue>[], AsyncRule<FieldValue>[]]

export default function createFieldValidator<FormValue, FieldName extends DeepKeys<FormValue>>(
  ...syncRules: SyncRule<DeepValue<FormValue, FieldName>>[]
): FieldValidators<FormValue, FieldName>
export default function createFieldValidator<FormValue, FieldName extends DeepKeys<FormValue>>(
  syncRules: SyncRule<DeepValue<FormValue, FieldName>>[],
  asyncRules: AsyncRule<DeepValue<FormValue, FieldName>>[]
): FieldValidators<FormValue, FieldName>
export default function createFieldValidator<FormValue, FieldName extends DeepKeys<FormValue>>(
  ...args: CreateFieldValidatorArgs<DeepValue<FormValue, FieldName>>
): FieldValidators<FormValue, FieldName> {
  const syncRules = (Array.isArray(args[0]) ? args[0] : args) as SyncRule<DeepValue<FormValue, FieldName>>[]
  const asyncRules = (Array.isArray(args[1]) ? args[1] : []) as AsyncRule<DeepValue<FormValue, FieldName>>[]

  const onBlur: FieldValidateOrFn<FormValue, FieldName> = ({ value }) => {
    for (const rule of syncRules) {
      const result = rule(value)
      if (result) return result
    }

    return undefined
  }

  const onBlurAsync: FieldAsyncValidateOrFn<FormValue, FieldName> = async ({ value }) => {
    for (const rule of asyncRules) {
      const result = await rule(value)
      if (result) return result
    }

    return undefined
  }

  const onChange: FieldValidateOrFn<FormValue, FieldName> = ({ value, fieldApi }) => {
    // Do not show errors when user is typing
    if (fieldApi.state.meta.errors.length === 0) {
      return undefined
    }

    // Revalidate and show different error or hide error message if new field value is valid
    for (const rule of syncRules) {
      const result = rule(value)
      if (result) return result
    }

    fieldApi.setErrorMap({
      onBlur: undefined,
    })

    return undefined
  }

  const onChangeAsync: FieldAsyncValidateOrFn<FormValue, FieldName> = async ({ value, fieldApi }) => {
    // Do not show errors when user is typing
    if (fieldApi.state.meta.errors.length === 0) {
      return undefined
    }

    // Revalidate and show different error or hide error message if new field value is valid
    for (const rule of asyncRules) {
      const result = await rule(value)
      if (result) return result
    }

    fieldApi.setErrorMap({
      onBlur: undefined,
    })

    return undefined
  }

  return {
    onBlur,

    onBlurAsync: asyncRules.length ? onBlurAsync : undefined,

    onChange,

    onChangeAsync: asyncRules.length ? onChangeAsync : undefined,
  }
}
